import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { QueryKeys, SURVEY_ID_HEADER } from '../constants';
import { useFetch } from './utils';

function setSavedSegmentsQueryData(prev, data, response = prev?.response) {
  return {
    ...(prev || {}),
    response,
    data,
  };
}

export function usePersistedSegments({ surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.PERSISTED_SEGMENTS, { surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/surveys/${surveyId}/persisted-segments/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function usePersistedSegment({ surveyId, segmentId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.PERSISTED_SEGMENT, { surveyId, segmentId }],
    queryFn: () =>
      fetcher({
        path: `v1/surveys/${surveyId}/persisted-segments/${segmentId}`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useSavedSegments({ surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SAVED_SEGMENTS, { surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/surveys/${surveyId}/segments/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useCreatePersistedSegments(options = {}) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, data }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/persisted-segments/create/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
        method: 'POST',
        data: JSON.stringify(data),
      }),
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]),
    onError: () => message.error('Something went wrong creating segments'),
    ...options,
  });
}

export function useUpdatePersistedSegment(options = {}) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, segmentId, data }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/persisted-segments/${segmentId}/update/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
        method: 'PATCH',
        data: JSON.stringify(data),
      }),
    onMutate: async ({ surveyId, segmentId, data }) => {
      await queryClient.cancelQueries([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]);
      const prevSegments = queryClient.getQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]);
      queryClient.setQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }], (prev) => ({
        ...prev,
        data: prev.data.map((s) => (s.id === segmentId ? { ...s, ...data } : s)),
      }));
      return { prevSegments };
    },
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]),
    onError: (_error, { surveyId }, context) => {
      queryClient.setQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }], context.prevSegments);
      message.error('Something went wrong updating segment');
    },
    ...options,
  });
}

export function useDeletePersistedSegments(options = {}) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, segmentIds }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/persisted-segments/delete/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
        method: 'DELETE',
        data: JSON.stringify(segmentIds),
      }),
    onMutate: async ({ surveyId, segmentIds }) => {
      await queryClient.cancelQueries([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]);
      const prevSegments = queryClient.getQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]);
      await queryClient.setQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }], (prev) => ({
        ...prev,
        data: prev.data.filter((s) => !segmentIds.includes(s.id)),
      }));
      return { prevSegments };
    },
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]),
    onError: (_error, { surveyId }, context) => {
      queryClient.setQueryData([QueryKeys.PERSISTED_SEGMENTS, { surveyId }], context.prevSegments);
      message.error('Something went wrong deleting segments');
    },
    ...options,
  });
}

export function useClearAllPersistedSegments(options = {}) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/persisted-segments/clear/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
        method: 'DELETE',
      }),
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.PERSISTED_SEGMENTS, { surveyId }]),
    onError: () => message.error('Something went wrong clearing segments'),
    ...options,
  });
}

export function useCreateSavedSegment(options = {}) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, data }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/segments/create/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
        method: 'POST',
        data: JSON.stringify(data),
      }),
    onMutate: async ({ surveyId, data }) => {
      await queryClient.cancelQueries([QueryKeys.SAVED_SEGMENTS, { surveyId }]);
      const prevSegments = queryClient.getQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }]);
      const optimisticSegment = {
        id: `saved-segment-${Date.now()}`,
        ...data,
      };
      queryClient.setQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }], (prev) =>
        setSavedSegmentsQueryData(prev, [...(prev?.data || []), optimisticSegment]),
      );
      return { prevSegments, optimisticSegmentId: optimisticSegment.id };
    },
    onSuccess: (response, { surveyId }, context) => {
      queryClient.setQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }], (prev) => {
        const nextSegments = (prev?.data || []).filter(
          (segment) =>
            segment.id !== context?.optimisticSegmentId && segment.id !== response.data.id,
        );
        return setSavedSegmentsQueryData(prev, [...nextSegments, response.data], response.response);
      });
    },
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.SAVED_SEGMENTS, { surveyId }]),
    onError: (_error, { surveyId }, context) => {
      queryClient.setQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }], context?.prevSegments);
      message.error('Something went wrong creating saved segment');
    },
    ...options,
  });
}

export function useUpdateSavedSegment(options = {}) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, segmentId, data }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/segments/${segmentId}/update/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
        method: 'PATCH',
        data: JSON.stringify(data),
      }),
    onMutate: async ({ surveyId, segmentId, data }) => {
      await queryClient.cancelQueries([QueryKeys.SAVED_SEGMENTS, { surveyId }]);
      const prevSegments = queryClient.getQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }]);
      queryClient.setQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }], (prev) =>
        setSavedSegmentsQueryData(
          prev,
          prev?.data?.map((segment) =>
            segment.id === segmentId ? { ...segment, ...data } : segment,
          ) || [],
        ),
      );
      return { prevSegments };
    },
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.SAVED_SEGMENTS, { surveyId }]),
    onError: (_error, { surveyId }, context) => {
      queryClient.setQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }], context?.prevSegments);
      message.error('Something went wrong updating segment label');
    },
    ...options,
  });
}

export function useDeleteSavedSegment(options = {}) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyId, segmentId }) =>
      fetcher({
        path: `v1/surveys/${surveyId}/segments/${segmentId}/delete/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
        method: 'DELETE',
      }),
    onMutate: async ({ surveyId, segmentId }) => {
      await queryClient.cancelQueries([QueryKeys.SAVED_SEGMENTS, { surveyId }]);
      const prevSegments = queryClient.getQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }]);
      queryClient.setQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }], (prev) =>
        setSavedSegmentsQueryData(
          prev,
          prev?.data?.filter((segment) => segment.id !== segmentId) || [],
        ),
      );
      return { prevSegments };
    },
    onSettled: (_data, _error, { surveyId }) =>
      queryClient.invalidateQueries([QueryKeys.SAVED_SEGMENTS, { surveyId }]),
    onError: (_error, { surveyId }, context) => {
      queryClient.setQueryData([QueryKeys.SAVED_SEGMENTS, { surveyId }], context?.prevSegments);
      message.error('Something went wrong deleting segment');
    },
    ...options,
  });
}
